import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { useLazyQuery, useMutation } from "@apollo/client";
import _ from "lodash";
import qs from "query-string";

import SearchBar from "../SearchBar";
import { DegreeFilter } from "components/DegreeFilter";
import { ReactRegisterModal } from "../RegisterForm/components/ReactRegisterModal";
import { GlobalState } from "contexts/GlobalState";
import { GET_FAVORITES } from "../../schemas/favorite";
import { UPDATE_USER } from "schemas/profile";
import useSearch from "hooks/useSearch";
import useFocus from "hooks/useFocus";

import "./style.scss";

const FILTERS_GROUPS_NAMES = ["learning_aspect", "interest_areas", "mainSearch", "degree"];

export const SearchFilters = ({ labels }) => {
  const [acceptance, setAcceptance] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(!Utils.isMobile());
  const [showRegister, setShowRegister] = useState(false);
  const [clickedSearch, setClickedSearch] = useState(false);
  const [filterButton, setFilterButton] = useState(true);
  const needCustomFocus = labels.is_calculator && !Utils.isMobile();
  const { customFocusOnTab, customFocusOnShiftTab } = useFocus(needCustomFocus);
  const [globalSearch, setGlobalSearch] = GlobalState("search");
  const [globalUser, setGlobalUser] = GlobalState("user");
  const [globalCalc, setGlobalCalc] = GlobalState("calc");
  const [updateUser, { dataUser }] = useMutation(UPDATE_USER);
  const [getFavorites, { data }] = useLazyQuery(GET_FAVORITES, {
    fetchPolicy: "no-cache",
  });
  const [localSearch, setLocalSearch] = useState({});
  const { searchPrograms, writeOurFiltersToUrl } = useSearch();

  const uncheckAllCheckboxes = () => {
    const allCheckboxes = [...document.getElementsByClassName("custom-control-input")];
    allCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    })
  };

  const addUrlParamsToOurFilters = (searchFilters, filtersGroupsNames = FILTERS_GROUPS_NAMES) => {
    filtersGroupsNames.forEach(groupName => {
      let urlFiltersForSpecificGroup = Utils.getQueryStringValue(groupName);

      //check if we received query parameters for that group
      if (urlFiltersForSpecificGroup) {
        urlFiltersForSpecificGroup = urlFiltersForSpecificGroup.split(",");
        let ourFiltersForSpecificGroup = searchFilters[groupName] ?
          searchFilters[groupName].split(",") :
          [];
        //check for every filter if we already have it, and if not add that filter 
        urlFiltersForSpecificGroup.forEach(filter => {
          if (!ourFiltersForSpecificGroup.includes(filter)) {
            ourFiltersForSpecificGroup.push(filter)
          }
        })
        searchFilters[groupName] = ourFiltersForSpecificGroup.join(",")
        searchFilters.isSearch = true;
      }
    })
    return searchFilters;
  }

  const decideDefaultChecked = (filter, groupName) => {
    //check if the specific filter was sent in the url parameters
    let urlFiltersForSpecificGroup = Utils.getQueryStringValue(groupName);
    if (urlFiltersForSpecificGroup) {
      urlFiltersForSpecificGroup = urlFiltersForSpecificGroup.split(",");
      if (urlFiltersForSpecificGroup.includes(filter)) {
        return true;
      }
    }
    //check if the user saved this filter with save filter
    if (globalUser.user?.filters?.[groupName]) {
      const groupFilters = globalUser.user.filters[groupName].split(",");
      if (groupFilters.includes(filter)) {
        return true;
      }
      return false;
    }
    //still waiting for user to be loaded
    return undefined;
  }

  const handleSearchButtonClick = () => {
    const globalFilters = {
      ...globalSearch.filters,
      ...localSearch.filters,
    }
    writeOurFiltersToUrl(globalFilters);
    setClickedSearch(true);
    searchPrograms(_.clone(globalFilters));
  }

  //read to the user that new results has been received
  useEffect(() => {
    if (clickedSearch) {
      const resultsElement = document.getElementsByClassName("search-results")[0];
      resultsElement?.focus();
      setClickedSearch(false);
    }
  }, [globalSearch.results])

  useEffect(() => {
    getFavorites();
  }, []);

  //set acceptance filter to be equal adapter score (only in calc page)
  useEffect(() => {
    if (labels.is_calculator && globalCalc?.hatamaCalcScore) {
      setAcceptance(globalCalc.hatamaCalcScore)
    }
  }, [globalCalc])

  //main useEffect, load filters, add to query string, and search for programs
  useEffect(() => {
    if (!globalUser.loading) {
      //get the search filters from globalSearch
      let ourFilters = { ...globalSearch.filters };
      //if there's a user, get the filters from the user as well
      if (globalUser?.user?.filters) {
        ourFilters = { ...ourFilters, ...globalUser.user.filters }
      }
      //if we're in a faculty page, add faculty filter
      if (labels.faculty_id) {
        ourFilters.faculty = labels.faculty_id;
        ourFilters.isSearch = true;
      }
      //if we're in a external faculty page, add faculty_nid filter
      if (labels.faculty_nid && !labels.faculty_id) {
        ourFilters.externalFaculty = labels.faculty_nid;
        ourFilters.isSearch = true;
      }
      //add URL params to our filters
      ourFilters = addUrlParamsToOurFilters({ ...ourFilters });
      //set our localSearch object
      if (_.isEmpty(localSearch)) {
        setLocalSearch({ ...(_.cloneDeep(globalSearch)), filters: ourFilters });
      }

      //write our filters to URL params
      writeOurFiltersToUrl(ourFilters);

      //after we added user filters and URL filters, search for the program (which will also set globalSearch)
      searchPrograms(ourFilters);
    }
  }, [globalUser.loading])

  const handleChangeFilters = (e, item, field) => {
    setFilterButton(false);
    const searchFilters = { ...localSearch.filters };
    let filter = searchFilters[field]
      ? searchFilters[field].split(",")
      : [];
    if (e.target.checked) {
      filter.push(item);
    } else {
      filter.splice(filter.indexOf(item), 1);
    }
    searchFilters[field] = filter.join(",");
    if (searchFilters[field] === "") {
      delete searchFilters[field];
    }
    //writeOurFiltersToUrl(searchFilters);
    setLocalSearch({
      ...localSearch,
      filters: searchFilters
    });


  };

  const handleChangeFavoritesCheckbox = (e, item, field) => {
    let searchFilters = { ...localSearch.filters };
    let filter = localSearch.filters[field]
      ? localSearch.filters[field].split(",")
      : [];
    if (e.target.checked) {
      filter.push(item);
      searchFilters[field] = filter.join(",");
    } else searchFilters = _.omit(localSearch.filters, "nid");

    setLocalSearch({
      ...localSearch,
      filters: searchFilters
    });
  };

  const cleanSearchParams = () => {
    uncheckAllCheckboxes();
    let newFilters = {}
    //clean query string
    Utils.setQueryStringWithoutPageReload("");
    if (labels.is_calculator) {
      newFilters = { acceptance: globalSearch.filters.acceptance }
    } else if (labels.faculty_id) {
      newFilters = { faculty: labels.faculty_id }
    }
    setLocalSearch({
      ...globalSearch,
      filters: newFilters,
    });
    searchPrograms(newFilters);
    if (globalUser.user) {
      saveSearchParams({});
    }
  };

  const saveSearchParams = (filters) => {
    if (globalUser.user) {
      if (labels.is_calculator && filters.acceptance) {
        delete filters.acceptance;
      }
      updateUser({
        variables: {
          psychometric: globalUser.user.psychometric ?? "",
          maturity: globalUser.user.maturity ?? "",
          adapter: globalUser.user.adapter ?? "",
          filters: _.cloneDeep(filters),
          registrationFormChoices:
            globalUser.user.registrationFormChoices ?? {},
        },
      });
    } else {
      setShowRegister(true);
    }
  };

  const onChangeAcceptance = (e, item, field) => {
    setAcceptance(e.target.value);
    if (localSearch.filters[field]) {
      setLocalSearch({
        ...localSearch,
        filters: {
          ...localSearch.filters,
          [field]: e.target.value
        }
      })
    }
  };
  const shouldHideWidget = () => {
    return (!globalCalc.showOtherWidgets && labels.is_calculator);
  }

  const handleOpenFilters = () => {
    if (Utils.isMobile()) {
      setFiltersOpen((prevState) => !prevState)
    }
  }

  const findPlaceholder = () => {
    if (labels.is_calculator) {
      return labels.SearchBar.labels.placeholderInCalc;
    }
    if (labels.faculty_id) {
      return labels.SearchBar.labels.placeholderInFaculty;
    }
    return labels.searchBar ?? "";
  }

  const handleKeyDown = (e) => {
    if (e.keyCode == 13 || e.keyCode == 32) {
      e.preventDefault();
      handleOpenFilters();
    }
  }

  return (
    <>
      {/* If asking to return search filters to mobile, delete this Degree Filter component and unslash the degree component in line 284*/}
      {Utils.isMobile() && !labels.is_calculator && !labels.is_global_search && (
        <Row>
          <DegreeFilter {...labels.terms} />
        </Row>
      )}
      {/* If asking to return search filters to mobile, delete the isMobile query benath*/}
      {!Utils.isMobile() && (
        <Container className={`filters ${shouldHideWidget() ? "hide" : labels.is_calculator ? "in-calc" : ""}`}>
          <div>
            {/*Utils.isMobile() && !labels.is_calculator && !labels.is_global_search && (
              <Row>
                <DegreeFilter {...labels.terms} />
              </Row>
            )*/}
            <Row>
              <Col className="filters-col-wrapper">
                <div className="section">
                  {Utils.isMobile() && labels.faculty_id && (
                    <SearchBar
                      labels={labels.SearchBar.labels}
                      placeholder={findPlaceholder()}
                    ></SearchBar>
                  )}
                  <div
                    className="filter-by-btn-wrapper"
                    typeof={Utils.isMobile() ? "button" : undefined}
                    onClick={() => handleOpenFilters()}
                    onKeyDown={(e) => handleKeyDown(e)}>
                    <h2
                      tabIndex="0"
                      id="filterBy" aria-label={labels.label}
                      onKeyDown={needCustomFocus ? (event) => customFocusOnShiftTab(event, "scrollLeft") : undefined}>
                      {labels.label}
                    </h2>
                  </div>
                </div>
                <div>
                  {filtersOpen && (
                    <>
                      {!Utils.isMobile() && (
                        <>
                          <div className="right-search-bar">
                            <SearchBar
                              labels={labels.SearchBar.labels}
                              placeholder={findPlaceholder()}
                            ></SearchBar>
                          </div>
                          <div className="btn save-filters section d-flex">
                            <button
                              id="saveFiltersBtn"
                              className="clean-save-btns"
                              tabIndex="0"
                              aria-labelledby="saveFiltersBtn"
                              onClick={() => saveSearchParams(localSearch.filters)}
                            >
                              {labels.save}
                            </button>
                            <button
                              id="cleanFiltersBtnDesk"
                              className="clean-save-btns"
                              tabIndex="0"
                              aria-labelledby="cleanFiltersBtnDesk"
                              onClick={(e) => cleanSearchParams()}>
                              {labels.clean}
                            </button>
                          </div>
                        </>
                      )}
                      <div className="section">
                        <h3 className="filters-group-title" tabIndex="0">{labels.priority}</h3>
                        <Row className="single-filter">
                          <Form.Check
                            custom
                            aria-labelledby="acceptanceLabel"
                            defaultChecked={globalUser.user?.filters?.acceptance}
                            onClick={(e) => handleChangeFilters(e, acceptance, "acceptance")}
                          ></Form.Check>
                          <div id="acceptanceLabel" className="name">{labels.acceptance}</div>
                          <Form.Control
                            value={acceptance || ""}
                            id="acceptanceInput"
                            type="number"
                            aria-label={labels.acceptanceInput}
                            className="acceptance"
                            onChange={(e) =>
                              onChangeAcceptance(e, acceptance, "acceptance")
                            }
                            required
                          />
                        </Row>
                        {/* Nirit asked to delete this filter so i'm slashing it
                      <Row className="single-filter">
                        <Form.Check
                          custom
                          aria-labelledby="appropriate"
                          defaultChecked={globalUser.user?.filters?.adapter}
                          onClick={(e) =>
                            handleChangeFilters(
                              e,
                              globalUser.user && globalUser.user.adapter,
                              "adapter"
                            )
                          }
                        ></Form.Check>
                        <div id="appropriate" className="name">{labels.appropriate}</div>
                      </Row>
                    */}
                        <Row>
                          <Form.Check
                            custom
                            aria-labelledby="markedFav"
                            defaultChecked={globalUser.user?.filters?.nid}
                            onClick={(e) =>
                              handleChangeFavoritesCheckbox(
                                e,
                                data &&
                                data.getFavorites.body.map((q) => Number(q.nid)),
                                "nid"
                              )
                            }
                          ></Form.Check>
                          <div id="markedFav" className="name">{labels.favorite}</div>
                        </Row>
                        <Row>
                          <Form.Check
                            custom
                            aria-labelledby="openReg"
                            defaultChecked={globalUser.user?.filters?.endRegistration}
                            onClick={(e) =>
                              handleChangeFilters(
                                e,
                                Math.floor(Date.now() / 1000),
                                "endRegistration"
                              )
                            }
                          ></Form.Check>
                          <div id="openReg" className="name">{labels.open}</div>
                        </Row>
                      </div>
                      {/* learning aspect checkboxes*/}
                      {/* <div className="section">
                    <div className="filters-group-title" tabIndex="0">{labels.program_type}</div>
                      {labels.terms.learning_aspect.map((item, index) => (
                        <Row key={item.name}>
                          <Form.Check
                            custom
                            aria-labelledby={`learnAspect${index}`}
                            type="checkbox"
                            onClick={(e) =>
                              handleChangeFilters(e, item.name, "learning_aspect")
                            }
                            defaultChecked={ decideDefaultChecked(item.name,"learning_aspect") }
                          />
                          <div id={`learnAspect${index}`} className={"name"}>{item.name}</div>
                        </Row>
                      ))}
                    </div> */}
                      <div className="section">
                        <h3 className="filters-group-title" tabIndex="0">{labels.Interest}</h3>
                        {labels.terms.interest_areas.map((item, index) => (
                          <Row key={item.name}>
                            <Form.Check
                              custom
                              type="checkbox"
                              aria-labelledby={`interestArea${index}`}
                              onClick={(e) =>
                                handleChangeFilters(e, item.name, "interest_areas")
                              }
                              defaultChecked={decideDefaultChecked(item.name, "interest_areas")}
                            ></Form.Check>
                            <div id={`interestArea${index}`} className={"name"}>{item.name}</div>
                          </Row>
                        ))}
                      </div>
                      <button
                        disabled={filterButton}
                        id="sortBtn"
                        aria-labelledby="sortBtn"
                        type="button"
                        className="btn btn-primary  btn-search"
                        onClick={handleSearchButtonClick}
                        onKeyDown={needCustomFocus ? (event) => customFocusOnTab(event, "title_searchResult0") : undefined}
                      >
                        <span className="btn-value">{labels.sort_button}</span>
                      </button>
                      {Utils.isMobile() && (
                        <button
                          aria-labelledby="cleanFiltersBtnMob"
                          id="cleanFiltersBtnMob"
                          className="clean-save-btns"
                          tabIndex="0"
                          onClick={(e) => cleanSearchParams()}>
                          {labels.clean}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </div>
          <ReactRegisterModal
            show={showRegister}
            setShow={setShowRegister}
            labels={labels.RegisterForm.labels}
            lastAction={{
              action: saveSearchParams,
              params: localSearch.filters
            }}
          />
        </Container>
      )}
    </>
  );
};


